import { MouseEvent, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Field,
  FileField,
  ImageField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';

import { icon_close_medium, icon_play } from '@gn-audio/icons';
import { JabraIcon } from '@gn-audio/jss-components';

import { getSharedProperty } from 'common/getters';
import VideoBackground from 'composables/Banners/VideoBanner/VideoBackground';
import VideoEmbed from 'composables/Product/VideoEmbed/VideoEmbed';
import { ComponentProps } from 'lib/component-props';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

type VideoSpotProps = {
  fields: {
    videoLg: FileField;
    videoMd: FileField;
    videoXs: FileField;
    image: ImageField;
    text: TextField;
    textShared: TextField;
    backgroundContrast: Field<number>;
    youtubeVideoID: TextField;
    youkuVideoID: TextField;
    reducedWidth: Field<boolean>;
    backgroundColor: TextField;
    backgroundColorShared: TextField;
  };
} & ComponentProps;

const VideoSpot = ({ fields }: VideoSpotProps) => {
  const [activeVideoId, setActiveVideoId] = useState<string | number | null>(null);

  const { t } = useFallbackTranslation();
  const {
    videoLg,
    videoMd,
    videoXs,
    image,
    backgroundContrast,
    reducedWidth,
    youtubeVideoID,
    youkuVideoID,
  } = fields;

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const modalRef = useRef<HTMLDivElement>(null);

  const handleOpenVideo = (event: MouseEvent<HTMLButtonElement>) => {
    triggerRef.current = event.currentTarget;
    setActiveVideoId(youtubeVideoID?.value || youkuVideoID?.value || null);
  };

  const closeVideoModal = () => {
    setActiveVideoId(null);
    triggerRef.current?.focus();
  };

  const handleClickOutside = (event: MouseEvent<HTMLDivElement>) => {
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      closeVideoModal();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeVideoModal();
    };
    if (activeVideoId) {
      modalRef.current?.focus();
      document.addEventListener('keydown', handleKeyDown);
      document.body.setAttribute('data-scroll-locked', '1');

      closeButtonRef.current?.focus();
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.removeAttribute('data-scroll-locked');
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.removeAttribute('data-scroll-locked');
    };
  }, [activeVideoId]);

  const title = getSharedProperty(fields, 'text')?.value;
  const spotBackgroundColor = getSharedProperty(fields, 'backgroundColor')?.value || 'transparent';
  const hasVideoSrc = youtubeVideoID?.value || youkuVideoID?.value;

  return (
    <div style={{ background: spotBackgroundColor }}>
      <VideoBackground
        videoLg={videoLg}
        videoMd={videoMd}
        videoXs={videoXs}
        image={image}
        backgroundContrast={backgroundContrast.value}
        reducedWidth={reducedWidth.value}
        videoContainerClass="aspect-[9/16] md:aspect-video"
      >
        {title && (
          <div
            dangerouslySetInnerHTML={{ __html: title as string }}
            className="mb-6 w-full px-6 text-center text-white md:mb-11"
          />
        )}
        {hasVideoSrc && (
          <button
            ref={triggerRef}
            onClick={handleOpenVideo}
            aria-label={t('system.OpenPlayer')}
            className="group flex h-[120px] w-[89px] items-center justify-center rounded-full bg-yellow/20 transition-colors hover:bg-gray-700/20"
          >
            <div className="flex h-[96px] w-[65px] items-center justify-center rounded-full bg-yellow transition-colors hover:bg-gray-700 hover:text-white">
              <JabraIcon viewBox="0 0 24 24" className="size-7" Icon={icon_play} />
            </div>
          </button>
        )}

        {activeVideoId &&
          createPortal(
            <div
              ref={modalRef}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
              role="dialog"
              aria-modal="true"
              aria-label={t('system.VideoPlayer')}
            >
              <div onClick={handleClickOutside} className="absolute inset-0" />
              <button
                onFocus={() => {
                  const videoEmbed = modalRef.current?.querySelector('iframe');
                  videoEmbed?.focus();
                }}
                className="sr-only"
                aria-hidden="true"
              />
              <button
                aria-label={t('system.ClosePlayer')}
                ref={closeButtonRef}
                onClick={closeVideoModal}
                className="absolute right-6 top-6 flex size-12 items-center justify-center rounded-full bg-yellow md:right-9 md:top-9 md:h-20 md:w-20"
              >
                <JabraIcon Icon={icon_close_medium} />
              </button>
              <div className="relative w-full max-w-[90%] md:max-w-[75%]" ref={containerRef}>
                <VideoEmbed
                  className="aspect-video size-full bg-black"
                  youkuId={youkuVideoID?.value as string}
                  youtubeId={youtubeVideoID?.value as string}
                />
                <button
                  onFocus={() => {
                    closeButtonRef.current?.focus();
                  }}
                  className="sr-only"
                  aria-hidden="true"
                />
              </div>
            </div>,
            document.body
          )}
      </VideoBackground>
    </div>
  );
};

export default withDatasourceCheck()<VideoSpotProps>(VideoSpot);
